// ThemeContext — provides the active visual theme to the whole app.
//
// Five themes registered. The two new (daylight / midnight) are the
// recommended pair from the UI redesign; the legacy three remain for
// backward compat (Clara Live mobile forces warm-leather, and any
// previously-saved user preference keeps working).
//
// Active theme is applied via `data-theme` on <html> and the CSS
// variables in src/index.css automatically flip.
//
// Persistence strategy (device-local + profile-synced):
//   1. On mount, read localStorage. If present, apply immediately.
//   2. When session is available, fetch profiles.theme_preference
//      and take that as the source of truth (profile beats device).
//   3. On user change (setTheme), write to localStorage AND update
//      profiles.theme_preference so every device sees the new choice.
//
// An inline <script> in index.html applies the localStorage theme
// before React mounts, preventing flash-of-unstyled-content.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";

export type Theme =
  | "daylight"
  | "midnight"
  | "market-green"
  | "warm-leather"
  | "ivory-blue";
export const VALID_THEMES: Theme[] = [
  "daylight",
  "midnight",
  "market-green",
  "warm-leather",
  "ivory-blue",
];
export const DEFAULT_THEME: Theme = "daylight";

const LOCAL_STORAGE_KEY = "rb-theme";

export interface ThemeMeta {
  id: Theme;
  label: string;
  description: string;
  mode: "dark" | "light";
  /** "current" themes are the redesigned daylight/midnight pair.
      "legacy" themes are preserved for backward compat but new users
      land on the current pair. */
  group: "current" | "legacy";
}

export const THEME_META: Record<Theme, ThemeMeta> = {
  daylight: {
    id: "daylight",
    label: "Daylight",
    description: "Warm off-white canvas with forest-green accents. The default.",
    mode: "light",
    group: "current",
  },
  midnight: {
    id: "midnight",
    label: "Midnight",
    description: "Institutional navy surfaces with emerald accents. Built for long sessions.",
    mode: "dark",
    group: "current",
  },
  "market-green": {
    id: "market-green",
    label: "Market Green",
    description: "Legacy. Classic forest green with gold accents.",
    mode: "dark",
    group: "legacy",
  },
  "warm-leather": {
    id: "warm-leather",
    label: "Warm Leather",
    description: "Legacy. Rich brown with ivory and antique brass.",
    mode: "dark",
    group: "legacy",
  },
  "ivory-blue": {
    id: "ivory-blue",
    label: "Paper",
    description: "Legacy. Warm paper canvas with forest green and amber accents.",
    mode: "light",
    group: "legacy",
  },
};

function isValidTheme(v: unknown): v is Theme {
  return typeof v === "string" && VALID_THEMES.includes(v as Theme);
}

function readLocal(): Theme | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (isValidTheme(raw)) return raw;
    return null;
  } catch {
    return null;
  }
}

function writeLocal(theme: Theme) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, theme);
  } catch {
    // localStorage unavailable (private mode, quota) — skip silently.
  }
}

function applyDocumentTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  /** Override the document theme while a specific subtree is mounted
      (used by Clara mobile to force warm-leather regardless of user
      preference). Returns a restore function. */
  overrideDocumentTheme: (t: Theme) => () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Read localStorage synchronously during first render so the initial
  // DOM-attribute matches what the FOUC script already applied. This
  // avoids a double-flash if localStorage had "ivory-blue" but React
  // defaults to "warm-leather".
  const [theme, setThemeState] = useState<Theme>(
    () => readLocal() ?? DEFAULT_THEME,
  );
  const { session } = useUserProfile();

  // Apply theme whenever it changes (including on mount)
  useEffect(() => {
    applyDocumentTheme(theme);
  }, [theme]);

  // When session becomes available, fetch profile preference. Profile
  // wins over localStorage (user set their choice on another device).
  useEffect(() => {
    if (!session?.user.id) return;
    let cancelled = false;
    (async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("theme_preference")
        .eq("user_id", session.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.warn("[ThemeProvider] profile fetch:", error.message);
        return;
      }
      const profileTheme = data?.theme_preference;
      if (isValidTheme(profileTheme) && profileTheme !== theme) {
        setThemeState(profileTheme);
        writeLocal(profileTheme);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user.id]);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      writeLocal(next);
      // Persist to profile asynchronously — failures don't block the UX
      if (session?.user.id) {
        (async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { error } = await (supabase as any)
            .from("profiles")
            .update({ theme_preference: next, updated_at: new Date().toISOString() })
            .eq("user_id", session.user.id);
          if (error) {
            console.warn("[ThemeProvider] profile update:", error.message);
          }
        })();
      }
    },
    [session?.user.id],
  );

  const overrideDocumentTheme = useCallback(
    (t: Theme): (() => void) => {
      const previous = (document.documentElement.getAttribute("data-theme") as Theme) ?? theme;
      applyDocumentTheme(t);
      return () => {
        applyDocumentTheme(previous);
      };
    },
    [theme],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, overrideDocumentTheme }),
    [theme, setTheme, overrideDocumentTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}
